import { SdkContext } from '../internal'
import { listItems } from '@/lib/utils'
import type { CommunityAppearance } from '@/lib/appearance'
import type {
  AdminCommunityDetail,
  AdminMemberEntry,
  AdminCommunityListFilters,
  AdminCommunityListItem,
  ApproveResult,
  AuditEntry,
  KycLink,
  Pagination,
  RegistrationKey
} from '@/types/adminCommunities'

const REGISTRATION_PATH: Record<RegistrationKey, string> = {
  businessInformation: 'business-information',
  payoutSettings: 'payout-settings'
}

const cleanQuery = (params: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== false))

const paginationOf = (result: unknown): Pagination | undefined =>
  (result as { pagination?: Pagination } | null)?.pagination

/**
 * F-65: the SuperAdmin's Communities section. Every route answers 403 to
 * anyone else, and to the SuperAdmin outside the platform ("admin") community
 * (F-31 F2); the community is the target of the action, so it goes in the
 * path and no member is created for the SuperAdmin there.
 */
export class AdminCommunitiesResource {
  constructor(private ctx: SdkContext) {}

  /** Awaiting review first (oldest submission first), then the most recent. 20 per page by default. */
  async list(filters: AdminCommunityListFilters): Promise<{ items: AdminCommunityListItem[]; pagination?: Pagination }> {
    const result = await this.ctx.raw<unknown>(
      { httpMethod: 'GET', path: '/admin/communities' },
      { query: cleanQuery({ ...filters, awaitingReview: filters.awaitingReview ? 'true' : undefined }) }
    )
    return { items: listItems<AdminCommunityListItem>(result), pagination: paginationOf(result) }
  }

  /** Both registrations, the partners, signed documents and the recipient read live (or `stale`). */
  async get(communityId: string): Promise<AdminCommunityDetail> {
    return this.ctx.call({ httpMethod: 'GET', path: '/admin/communities/{communityId}' }, { communityId })
  }

  /**
   * Edits a registration without changing its status. `updatedAt` is the
   * version read (409 when it changed). On an approved pair with a recipient,
   * the provider is updated first and a refusal (422) saves nothing.
   */
  async updateRegistration(communityId: string, registration: RegistrationKey, body: Record<string, unknown>) {
    return this.ctx.call<{ registration: RegistrationKey; changed: string[]; providerUpdated: boolean }>(
      { httpMethod: 'PATCH', path: `/admin/communities/{communityId}/${REGISTRATION_PATH[registration]}`, hasBody: true },
      { communityId, body }
    )
  }

  /** The single "Approve": whatever is in review or draft; the pair that completes creates the recipient. */
  async approve(communityId: string): Promise<ApproveResult> {
    return this.ctx.call({ httpMethod: 'POST', path: '/admin/communities/{communityId}/approve', hasBody: true }, { communityId, body: {} })
  }

  async reject(communityId: string, registration: RegistrationKey, reason: string) {
    return this.ctx.call<{ registration: RegistrationKey; status: string; rejectionReason: string }>(
      { httpMethod: 'POST', path: `/admin/communities/{communityId}/${REGISTRATION_PATH[registration]}/reject`, hasBody: true },
      { communityId, body: { reason } }
    )
  }

  /** "Sincronizar de novo". 409 without the approved pair. */
  async syncRecipient(communityId: string) {
    return this.ctx.call<{ salesEnabled?: boolean; salesDisabled?: boolean }>(
      { httpMethod: 'POST', path: '/admin/communities/{communityId}/recipient/sync', hasBody: true },
      { communityId, body: {} }
    )
  }

  /**
   * F-31 F2 "Entrar": a one-time link (5 minutes) to the community's own domain
   * that opens a session as its owner. 409 when the community has no owner.
   */
  async enter(communityId: string): Promise<{ url: string; expiresAt: string }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/admin/communities/{communityId}/enter', hasBody: true }, { communityId, body: {} })
  }

  async kycLink(communityId: string): Promise<KycLink> {
    return this.ctx.call({ httpMethod: 'POST', path: '/admin/communities/{communityId}/recipient/kyc-link', hasBody: true }, { communityId, body: {} })
  }

  /** F-191: the community's appearance, with the dark mode switch (`darkModeAllowed`). */
  async getAppearanceAccess(communityId: string): Promise<{ appearance: CommunityAppearance }> {
    return this.ctx.call({ httpMethod: 'GET', path: '/admin/communities/{communityId}/appearance-access' }, { communityId })
  }

  /** F-191: turns dark mode on or off for the community; off keeps its mode and shows it light. */
  async setAppearanceAccess(communityId: string, darkModeAllowed: boolean): Promise<{ changed: boolean; appearance: CommunityAppearance }> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/admin/communities/{communityId}/appearance-access', hasBody: true }, { communityId, body: { darkModeAllowed } })
  }

  /** F-86: whether the community is suspended for new members and new sales, since when, by whom and why. */
  async getMemberEntry(communityId: string): Promise<AdminMemberEntry> {
    return this.ctx.call({ httpMethod: 'GET', path: '/admin/communities/{communityId}/member-entry' }, { communityId })
  }

  /** F-86: suspends (`true`) or lifts (`false`); the reason (10 to 2,000 characters) is required both ways. */
  async setMemberEntry(communityId: string, suspended: boolean, reason: string): Promise<AdminMemberEntry & { changed: boolean }> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/admin/communities/{communityId}/member-entry', hasBody: true },
      { communityId, body: { suspended, reason } }
    )
  }

  async audit(communityId: string, params: { page?: number; limit?: number } = {}): Promise<{ items: AuditEntry[]; pagination?: Pagination }> {
    const result = await this.ctx.raw<unknown>(
      { httpMethod: 'GET', path: '/admin/communities/{communityId}/audit' },
      { communityId, query: cleanQuery(params) }
    )
    return { items: listItems<AuditEntry>(result), pagination: paginationOf(result) }
  }
}
